import { useState } from "react";
import { Loader2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { BespokeFields } from "@/components/editor/BespokeFields";

const Label = ({ children }) => <label className="text-[11px] tracking-[0.2em] uppercase block mb-2">{children}</label>;

const Highlights = ({ value, onChange }) => {
  const [t, setT] = useState("");
  const add = () => { if (t.trim()) { onChange([...value, t.trim()]); setT(""); } };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="e.g. Boucle fabric, modular, rounded bolster arms" data-testid="highlight-input" />
        <Button variant="outline" onClick={add} data-testid="highlight-add-button"><Plus size={14} /></Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {value.map((h, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-[var(--ink)] text-[var(--cream)] text-xs px-3 py-1.5" data-testid={`highlight-chip-${i}`}>
            {h}<button onClick={() => onChange(value.filter((_, j) => j !== i))}><X size={11} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const EMPTY_ROW = { name: "", dimensions: "", w: "", d: "", h: "", price_select: "", price_luxe: "" };
const fmtDims = (r) => ["w", "d", "h"].filter((k) => r[k] !== "" && r[k] != null).map((k) => `${k.toUpperCase()} ${r[k]}"`).join(" x ");
const COLS = "grid grid-cols-[1.3fr_0.6fr_0.6fr_0.6fr_1fr_1fr_28px] gap-2 min-w-[720px]";
const cell = "bg-transparent border-white/20 text-[var(--cream)] placeholder:text-white/30";

const NumCell = ({ value, onChange, testid, placeholder }) => (
  <Input type="number" inputMode="decimal" min="0" value={value ?? ""} placeholder={placeholder} className={cell} data-testid={testid}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
);

const ConfigTable = ({ rows, onChange, cat }) => {
  const set = (i, k, v) => onChange(rows.map((r, j) => {
    if (j !== i) return r;
    const n = { ...r, [k]: v };
    return ["w", "d", "h"].includes(k) ? { ...n, dimensions: fmtDims(n) } : n;
  }));
  const options = cat.configurations || [];
  const unused = options.filter((o) => !rows.some((r) => r.name === o));
  const preset = () => onChange([...rows, ...unused.map((name) => ({ ...EMPTY_ROW, name }))]);
  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] p-4 sm:p-6 overflow-x-auto" data-testid="config-table">
      <div className={`${COLS} text-[10px] tracking-[0.2em] uppercase pb-3 border-b border-white/20`}>
        <span>Configuration</span><span>W (in)</span><span>D (in)</span><span>H (in)</span><span>Select (₹)</span><span>Luxe (₹)</span><span />
      </div>
      {rows.map((r, i) => (
        <div key={i} className="py-2 border-b border-white/10">
          <div className={COLS}>
            <Select value={r.name || undefined} onValueChange={(v) => set(i, "name", v)}>
              <SelectTrigger className={cell} data-testid={`config-name-${i}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {[...options, ...(r.name && !options.includes(r.name) ? [r.name] : [])].map((o) => (
                  <SelectItem key={o} value={o} disabled={o !== r.name && rows.some((x) => x.name === o)}>{o}{!options.includes(o) ? " (not in settings)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NumCell value={r.w} onChange={(v) => set(i, "w", v)} placeholder="72" testid={`config-w-${i}`} />
            <NumCell value={r.d} onChange={(v) => set(i, "d", v)} placeholder="36" testid={`config-d-${i}`} />
            <NumCell value={r.h} onChange={(v) => set(i, "h", v)} placeholder="28" testid={`config-h-${i}`} />
            <NumCell value={r.price_select} onChange={(v) => set(i, "price_select", v)} placeholder="72000" testid={`config-select-${i}`} />
            <NumCell value={r.price_luxe} onChange={(v) => set(i, "price_luxe", v)} placeholder="92000" testid={`config-luxe-${i}`} />
            <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-white/50 hover:text-white" data-testid={`config-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          {fmtDims(r) && <p className="text-[11px] text-white/50 mt-1.5 pl-1" data-testid={`config-dims-preview-${i}`}>Prints as: {fmtDims(r)}</p>}
        </div>
      ))}
      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="secondary" disabled={!unused.length} onClick={() => onChange([...rows, { ...EMPTY_ROW }])} data-testid="config-add-row"><Plus size={13} className="mr-1" />Add row</Button>
        <Button size="sm" variant="ghost" disabled={!unused.length} className="text-[var(--cream)] hover:bg-white/10 hover:text-white" onClick={preset} data-testid="config-preset">Add all {cat.name} configurations</Button>
      </div>
      {!options.length && <p className="text-xs text-white/60 mt-3" data-testid="config-no-options">No configurations set for {cat.name}. Add them in <Link to="/settings" className="underline">Settings → Standard configurations</Link>.</p>}
    </div>
  );
};

export const DetailsStep = ({ p, update, cat }) => {
  const [busy, setBusy] = useState(false);
  const write = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/copy", { name: p.name, highlights: p.highlights, materials: p.materials, notes: p.description, category_id: cat.id });
      update({ description: data.description || p.description, materials: p.materials || data.materials || "" });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10">
      <div className="space-y-6">
        <div><Label>Product name</Label><Input value={p.name} onChange={(e) => update({ name: e.target.value })} placeholder="Runa Upholstered Sofa" className="h-11" data-testid="product-name-input" /></div>
        <div><Label>Product code · assigned automatically</Label>
          <Input value={p.code} readOnly className="bg-[var(--paper)] font-semibold tracking-wider cursor-default" data-testid="product-code-input" /></div>
        <div><Label>Key highlights — AI uses these to pick &amp; crop photos</Label><Highlights value={p.highlights} onChange={(highlights) => update({ highlights })} /></div>
        <div><Label>Materials</Label><Input value={p.materials} onChange={(e) => update({ materials: e.target.value })} placeholder="Boucle upholstery, solid sheesham frame" data-testid="product-materials-input" /></div>
        <div>
          <div className="flex items-center justify-between mb-2"><Label>Description / write-up</Label>
            <Button size="sm" variant="ghost" onClick={write} disabled={busy || !p.name} className="text-[var(--terracotta)] -mt-2" data-testid="ai-write-button">
              {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />}Write with AI</Button></div>
          <Textarea rows={4} value={p.description} onChange={(e) => update({ description: e.target.value })} placeholder="Notes or a short write-up. AI can polish it." data-testid="product-description-input" />
        </div>
        {cat.style !== "bespoke" && <div className="flex items-center gap-3">
          <Switch checked={p.show_writeup} onCheckedChange={(v) => update({ show_writeup: v })} data-testid="show-writeup-switch" />
          <span className="text-sm">Print description &amp; materials under the price table</span>
        </div>}
      </div>
      <div>
        {cat.style === "bespoke" ? <BespokeFields p={p} update={update} cat={cat} /> : <>
          <h2 className="text-base md:text-lg font-bold mb-1">Configurations, dimensions &amp; price</h2>
          <p className="text-xs text-[var(--muted-ink)] mb-4">Enter numbers only — dimensions in inches. Prints as W 72" x D 36" x H 28" and Rs. 72,000.</p>
          <ConfigTable rows={p.configurations} onChange={(configurations) => update({ configurations })} cat={cat} />
        </>}
      </div>
    </div>
  );
};
